import { useState, useCallback, MutableRefObject } from "react";
import { geojsonToArcGIS } from "@terraformer/arcgis";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { getArcGISGeometryType, getSymbolForGeometryType } from "../../utils/arcgisHelper";
import { FeatureTracking } from "./useSketchEvents";
import { LayerFieldDefinition } from "../../queries/maps";
import { GeoJSONSchema } from "zod-geojson";
import { useToasts } from "react-toast-notifications";
import { kml } from "@tmcw/togeojson";
import { ToastStatuses } from "../../../../components/global_components/Toast";

interface UseImportLayerOptions {
  editLayerRef: MutableRefObject<GraphicsLayer | null>;
  setFeatureTracking: (tracking: FeatureTracking | ((prev: FeatureTracking) => FeatureTracking)) => void;
  maxFeatureIndex: MutableRefObject<number>;
  fieldDefinitions: LayerFieldDefinition[];
  handleAddColumn: (name: string, isPrimary?: boolean) => LayerFieldDefinition;
  markFeatureAsChangedDebounced: ((graphicId: string) => void) | null;
}

export function useImportLayer({
  editLayerRef,
  setFeatureTracking,
  maxFeatureIndex,
  fieldDefinitions,
  handleAddColumn,
  markFeatureAsChangedDebounced,
}: UseImportLayerOptions) {
  const [isImporting, setIsImporting] = useState(false);
  const [fileContent, setFileContent] = useState<Record<string, any> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileType, setFileType] = useState<"geojson" | "kml" | null>(null);
  const [originalKML, setOriginalKML] = useState<Document | null>(null);
  const { addToast } = useToasts();

  const addErrorToast = (message: string) => {
    addToast(message, { appearance: ToastStatuses.Error, autoDismiss: true });
  };

  const validateGeoJSON = (geoJSON: any, showToast = true): boolean => {
    try {
      if (!geoJSON || typeof geoJSON !== "object") throw new Error("Invalid GeoJSON: Not a valid JSON object");
      if (!("features" in geoJSON) || !Array.isArray(geoJSON.features) || !geoJSON.features.length) {
        throw new Error("Invalid GeoJSON: No features found in uploaded file");
      }
      try {
        GeoJSONSchema.parse(geoJSON);
      } catch (error: any) {
        throw new Error(`Invalid GeoJSON: ${error.message}`);
      }
      return true;
    } catch (error) {
      console.error(error);
      if (showToast) addErrorToast(`Error validating GeoJSON, please check the file format`);
      return false;
    }
  };

  const manicureFeature = (feature: any): any => {
    // Handle GeometryCollection by converting to a single Polygon
    try {
      if (feature.geometry.type === "GeometryCollection") {
        feature.geometry = {
          type: "Polygon",
          coordinates: feature.geometry.geometries.flatMap((g: any) => g.coordinates),
        };
      }
    } catch (e) {
      console.error("Error processing GeometryCollection:", e);
    }
    return feature;
  };

  const processGeoJSONFeature = (feature: any, index: number, fieldMappings: { [key: string]: number }): Graphic => {
    // Generate a unique ID for the graphic
    const graphicId = `imported-${crypto.randomUUID()}`;

    // Reshapes data to ensure compatibility with certain features (e.g., GeometryCollection)
    const geoJson = manicureFeature(feature);

    // Convert GeoJSON geometry to ArcGIS geometry
    const arcGISGeometry = geojsonToArcGIS(geoJson.geometry);

    const geometryType = getArcGISGeometryType(geoJson.geometry.type);

    // Map properties to field definitions
    const attributes: { [key: string]: any } = {
      graphicId,
      index: maxFeatureIndex.current + index,
    };

    // Map properties to field IDs
    if (geoJson.properties) {
      for (const [propName, propValue] of Object.entries(geoJson.properties)) {
        if (fieldMappings[propName] !== undefined) {
          attributes[fieldMappings[propName]] = `${propValue}`;
        }
      }
    }

    return new Graphic({
      geometry: {
        ...arcGISGeometry,
        type: geometryType,
      },
      attributes,
      symbol: getSymbolForGeometryType(geoJson.geometry.type),
    });
  };

  const importGeoJSONFeatures = (geoJSON: any, fieldMappings: { [key: string]: number }) => {
    if (!editLayerRef.current) return;

    try {
      setIsImporting(true);
      const features = geoJSON.type === "FeatureCollection" ? geoJSON.features : [geoJSON];
      const graphics: Graphic[] = [];

      // Process each feature
      features.forEach((feature: any, index: number) => {
        const graphic = processGeoJSONFeature(feature, index, fieldMappings);
        graphics.push(graphic);

        const graphicId = graphic.attributes.graphicId;
        setFeatureTracking((prev) => ({
          ...prev,
          [graphicId]: {
            isNew: true,
          },
        }));

        markFeatureAsChangedDebounced?.(graphicId);
      });

      maxFeatureIndex.current += graphics.length;
      editLayerRef.current.graphics.addMany(graphics);

      setIsImporting(false);
      setIsModalOpen(false);
      setFileContent(null);
      setFileType(null);

      return graphics.length;
    } catch (error) {
      addErrorToast(`Error importing features, please check the file format`);
      setIsImporting(false);
      return 0;
    }
  };

  const parseKML = (kmlString: string): Record<string, any> | null => {
    try {
      const kmlDoc = new DOMParser().parseFromString(kmlString, "text/xml");
      const geoJSON = kml(kmlDoc);
      if (validateGeoJSON(geoJSON, false)) {
        setOriginalKML(kmlDoc);
        return geoJSON;
      } else throw new Error("Invalid KML: Not a valid KML document");
    } catch (error) {
      console.log(error);
      addErrorToast(`Error parsing KML file, please check the file format`);
      return null;
    }
  };

  const handleFileUpload = useCallback((acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;

    const file = acceptedFiles[0];
    const fileName = file.name.toLowerCase();

    if (fileName.endsWith(".geojson") || fileName.endsWith(".json")) {
      setFileType("geojson");
    } else if (fileName.endsWith(".kml")) {
      setFileType("kml");
    } else {
      addErrorToast("Please upload a GeoJSON file (.geojson or .json) or a KML file (.kml)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const result = event.target?.result as string;

        if (fileName.endsWith(".kml")) {
          const geoJSON = parseKML(result);
          if (geoJSON) setFileContent(geoJSON);
        } else {
          const content = JSON.parse(result);
          if (validateGeoJSON(content)) setFileContent(content);
        }
      } catch (error) {
        addErrorToast(`Error parsing GeoJSON file, please check the file format`);
      }
    };
    reader.onerror = () => addErrorToast("Error reading file.");
    reader.readAsText(file);
  }, []);

  const createFieldMappings = (geoJSON: any): { [key: string]: number } => {
    const mappings: { [key: string]: number } = {};
    const existingFieldsByName = new Map(fieldDefinitions.map((fd) => [fd.customField.label, fd.customField.id]));
    let fieldShouldBePrimary = !existingFieldsByName.size;

    // Get sample properties from the first feature
    const sampleFeature = geoJSON.type === "FeatureCollection" ? geoJSON.features[0] : geoJSON;
    if (!sampleFeature || !sampleFeature.properties) return mappings;

    // Find or create field definitions for each property
    for (const propName of Object.keys(sampleFeature.properties)) {
      if (existingFieldsByName.has(propName)) {
        mappings[propName] = existingFieldsByName.get(propName)!;
      } else {
        const newField = handleAddColumn(propName, fieldShouldBePrimary);
        fieldShouldBePrimary = false;
        mappings[propName] = newField.customField.id;
      }
    }

    return mappings;
  };

  const handleImport = () => {
    if (!fileContent) return addErrorToast("No file uploaded");
    const fieldMappings = createFieldMappings(fileContent);
    const importedCount = importGeoJSONFeatures(fileContent, fieldMappings);
    if (importedCount && importedCount > 0) setIsModalOpen(false);
  };

  const wipeState = () => {
    setFileContent(null);
    setFileType(null);
    setOriginalKML(null);
  };

  return {
    isModalOpen,
    setIsModalOpen,
    isImporting,
    handleFileUpload,
    handleImport,
    fileContent,
    fileType,
    originalKML,
    wipeState,
  };
}
